/**
 * Auto Sourcing Results — progress dashboard + shared DealFlowCard grid.
 * Uses persisted opportunities + product_outcomes; never invents matches.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  LayoutGrid,
  Table2,
  Search,
} from "lucide-react";
import { DealFlowCard } from "@/components/deal-flow/DealFlowCard";
import { OpportunityDetailDialog } from "@/components/deal-flow/OpportunityDetailDialog";
import { mapDealFlowLead, emptyDealFlowLead } from "@/lib/dealFlow/adapter";
import { money } from "@/lib/format";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import AutoSourcingPagination from "./AutoSourcingPagination";
import RunSummaryCard, { ResultsSkeletonGrid } from "./RunSummaryCard";
import { readFilters, writeFilters } from "@/lib/autoSourcing/scanPersistence";
import { useSavedLeads } from "@/lib/leads/useSavedLeads";

/** Customer-facing Results filters — no Rejected / Needs Review / WINNING scorecard. */
const RESULT_FILTERS = [
  // Stage W: the qualifying grid comes FIRST. When the customer configured rules
  // this is the default tab and it is never silently replaced by Walmart Matches.
  { id: "qualifying", label: "Qualifying" },
  // Verified + potential. Distinct from "Verified Matches" below, and the
  // reason a summary that counts only verified shows a smaller number.
  { id: "walmart_matches", label: "All Matches" },
  { id: "matched", label: "Verified Matches" },
  { id: "potential", label: "Potential Matches" },
  { id: "all", label: "All Candidates" },
  { id: "positive_spread", label: "Positive Spread" },
  { id: "estimated", label: "Estimated Opportunities" },
];

/** Explore More Products — gated until continuation E2E tests pass. */
export const EXPLORE_MORE_ENABLED = false;

const OUTCOME_LABEL = {
  all_candidates_rejected: "All candidates rejected",
  no_candidates: "No Walmart listing",
  budget_exhausted: "Budget exhausted",
  identity_mismatch: "Product identity mismatch",
  insufficient_profit: "Insufficient profit",
  low_roi: "Low ROI",
  out_of_stock: "Out of stock",
  missing_price: "Missing catalog price",
  missing_identifiers: "Missing identifiers",
  provider_failure: "Provider failure",
};

function outcomeLabel(key) {
  if (!key) return "Rejected";
  return OUTCOME_LABEL[key] || String(key).replace(/_/g, " ");
}

function statusLabel(status) {
  const s = String(status || "queued");
  if (s === "running" || s === "collecting" || s === "matching") return "Running";
  if (s === "paused") return "Paused";
  if (s === "completed") return "Completed";
  if (s === "partial") return "Completed (partial)";
  if (s === "failed") return "Failed";
  if (s === "cancelled") return "Cancelled";
  if (s === "budget_exhausted") return "Budget Exhausted";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

const DISCOVERY_EMPTY = {
  no_products_found: {
    title: "Completed — no products found",
    next: "Try another category or search query for this retailer.",
  },
  discovery_unavailable: {
    title: "Discovery unavailable",
    next: "This retailer’s listing page could not be collected. Retry later or pick a different category URL.",
  },
  js_render_required: {
    title: "Listing needs JavaScript rendering",
    next: "This retailer serves its product grid client-side. The scan escalates to the rendering provider when the run's budget allows; otherwise increase the provider budget and re-run.",
  },
  render_unavailable: {
    title: "Rendering provider unavailable",
    next: "The listing requires rendering and the rendering provider was not available for this run. Check provider authorization, then re-run.",
  },
  provider_gateway_failure: {
    title: "Provider gateway failure",
    next: "The provider returned a gateway error (not a parsing problem). Retry later; repeated retries will not help.",
  },
  structured_unavailable: {
    title: "Structured data unavailable",
    next: "Target's structured product feed could not be used for this run. Check the structured-mode capability flag and the Redsky access key.",
  },
  empty_category: {
    title: "Category has no products",
    next: "This listing loaded correctly and genuinely contains no products. Choose another category.",
  },
  homepage_redirect: {
    title: "Retailer redirected to its home page",
    next: "The category URL no longer resolves to a listing. Update the category URL for this retailer.",
  },
  access_blocked: {
    title: "Access blocked",
    next: "The retailer blocked automated access. Retry later; do not re-run repeatedly.",
  },
  parser_failure: {
    title: "Parser failure",
    next: "The page loaded but product cards were not recognized. Try a different category.",
  },
  all_products_rejected: {
    title: "All products rejected",
    next: "Products were found but none passed eligibility (identity/price). Adjust category or filters.",
  },
  budget_exhausted: {
    title: "Budget exhausted",
    next: "Provider credits for this run were used up before products could be collected.",
  },
  provider_timeout: {
    title: "Provider timeout",
    next: "The listing fetch timed out. Retry with a narrower category.",
  },
};

function emptyDiscoveryCopy(run) {
  const status = String(run?.status || "").toLowerCase();
  if (["running", "collecting", "matching", "queued", "verifying"].includes(status)) {
    const catsDone = Number(run?.progress?.categories_scanned) || 0;
    const catsTotal = Number(run?.progress?.categories_total) || 0;
    const stage = String(run?.progress?.current_stage || status).replace(/_/g, " ");
    return {
      title: "Collecting products…",
      next:
        catsTotal > 0
          ? `Scanning categories ${catsDone}/${catsTotal} (${stage}). Product counts update as each page finishes.`
          : `Still ${stage}. Product counts update as category pages finish.`,
    };
  }
  const key = String(run?.discovery_outcome || run?.stop_reason || "").trim();
  if (DISCOVERY_EMPTY[key]) return DISCOVERY_EMPTY[key];
  if (key) {
    return {
      title: "No products discovered",
      next: `Reason: ${key.replace(/_/g, " ")}.`,
    };
  }
  return {
    title: "No products discovered",
    next: "The run finished without collecting products. Try another category or retailer.",
  };
}

const VERIFIED_MATCH_STATUSES = new Set(["verified", "exact"]);
const REVIEW_MATCH_STATUSES = new Set(["review", "probable"]);
const REJECT_OUTCOME_PREFIXES = [
  "all_candidates_rejected",
  "no_candidates",
  "qualification_rejected",
  "ineligible_product",
  "budget_exhausted",
];

function isRejectedOutcome(outcome) {
  const o = String(outcome || "").toLowerCase();
  return REJECT_OUTCOME_PREFIXES.some((p) => o === p || o.startsWith(`${p}:`));
}

/** Collected product lead — includes Walmart API candidate when discovery found one.
 * Accepts diagnostic product_outcomes rows and durable run-product documents. */
export function mapProductOutcomeToLead(row, run, idx) {
  const stableKey = row?.result_key || row?.source_product_id || idx;
  const id = `outcome-${run?.run_id || "run"}-${stableKey}`;
  const outcome = String(row?.outcome || "").trim();
  const walmartItemId =
    row?.walmart_item_id != null && String(row.walmart_item_id).trim()
      ? String(row.walmart_item_id).trim()
      : null;
  const walmartPrice =
    row?.walmart_price != null && Number.isFinite(Number(row.walmart_price))
      ? Number(row.walmart_price)
      : null;
  const rejectedOutcome = isRejectedOutcome(outcome);
  const verifiedOutcome = VERIFIED_MATCH_STATUSES.has(outcome.toLowerCase());
  const reviewOutcome = REVIEW_MATCH_STATUSES.has(outcome.toLowerCase());
  const persistedStatus = String(row?.opportunity_status || "").toUpperCase();
  let opportunityStatus = "INVALID";
  if (persistedStatus === "WINNING") opportunityStatus = "WINNING";
  else if (persistedStatus === "NEEDS_REVIEW" || persistedStatus === "ESTIMATED") {
    opportunityStatus = persistedStatus;
  } else if (reviewOutcome || (walmartItemId && rejectedOutcome)) {
    opportunityStatus = "NEEDS_REVIEW";
  }
  const sourcePrice =
    row?.source_price != null
      ? Number(row.source_price)
      : row?.price != null
        ? Number(row.price)
        : null;
  const grossFromRow =
    row?.gross_spread != null && Number.isFinite(Number(row.gross_spread))
      ? Number(row.gross_spread)
      : sourcePrice != null && walmartPrice != null
        ? Number((walmartPrice - sourcePrice).toFixed(2))
        : null;
  const profit =
    row?.estimated_profit != null && Number.isFinite(Number(row.estimated_profit))
      ? Number(row.estimated_profit)
      : null;
  const roi =
    row?.estimated_roi != null && Number.isFinite(Number(row.estimated_roi))
      ? Number(row.estimated_roi)
      : null;
  const margin =
    row?.estimated_margin != null && Number.isFinite(Number(row.estimated_margin))
      ? Number(row.estimated_margin)
      : null;
  const economicsComplete = row?.economics_complete === true;
  const matchState = String(row?.match_state || "").toLowerCase();
  const potentialMatch =
    matchState === "potential_match" || Boolean(walmartItemId && rejectedOutcome && !verifiedOutcome);
  return emptyDealFlowLead({
    id,
    sourceId: row?.source_id || null,
    // Stage Z: the retailer offer id. With source_id it forms the saved-lead
    // source_key, so one retailer offer is a distinct lead from another's.
    sourceProductId: row?.source_product_id ? String(row.source_product_id) : null,
    qualification: row?.qualification || null,
    sourceMerchant: row?.source_id || "Source",
    sourceTitle: row?.source_title || row?.title || null,
    sourceBrand: row?.source_brand || row?.brand || null,
    sourcePrice,
    // Genuine collected image/URL — never placeholders.
    sourceImage: row?.source_image || row?.image || row?.image_url || null,
    sourceUrl: row?.source_url || row?.url || null,
    upc: row?.upc || null,
    gtin: row?.gtin || null,
    // Walmart Marketplace API candidate (real item id / image) even when the
    // run did not persist a qualified opportunity.
    walmartItemId,
    walmartTitle: row?.walmart_title || null,
    walmartBrand: row?.walmart_brand || null,
    walmartPrice,
    walmartImage: row?.walmart_image || null,
    walmartUrl: walmartItemId ? `https://www.walmart.com/ip/${walmartItemId}` : null,
    rating:
      row?.walmart_rating != null && Number.isFinite(Number(row.walmart_rating))
        ? Number(row.walmart_rating)
        : null,
    reviews:
      row?.walmart_reviews != null && Number.isFinite(Number(row.walmart_reviews))
        ? Number(row.walmart_reviews)
        : null,
    spread: grossFromRow,
    profit,
    roi,
    margin,
    economicsComplete,
    // Pack Differs / unit pricing (persisted on the durable Results row).
    packDiffers:
      row?.pack_differs === true || row?.match_label_code === "pack_differs",
    priceBasis: row?.price_basis || "item",
    sourceUnitPrice:
      row?.source_unit_price != null ? Number(row.source_unit_price) : null,
    walmartUnitPrice:
      row?.walmart_unit_price != null ? Number(row.walmart_unit_price) : null,
    unitSpread: row?.unit_spread != null ? Number(row.unit_spread) : null,
    sourcePackLabel:
      row?.source_pack != null ? Number(row.source_pack) : null,
    walmartPackLabel:
      row?.walmart_pack != null ? Number(row.walmart_pack) : null,
    economicsKind:
      row?.economics_kind
      || (economicsComplete ? "complete_net_profit" : profit != null ? "fee_excluded_estimate" : "incomplete"),
    // Stage N: names the exact missing input when no number can be shown.
    economicsStatus: row?.economics_status || null,
    economicsMissingInput: row?.economics_missing_input || null,
    matchConfidence:
      row?.match_confidence != null && Number.isFinite(Number(row.match_confidence))
        ? Number(row.match_confidence)
        : null,
    matchLabel: row?.match_label || null,
    matchLabelCode: row?.match_label_code || null,
    // Preview candidate id is NOT a verified match — keep visible under Needs Review.
    potentialMatch,
    discoveryState: row?.discovery_state || "discovered",
    matchState: matchState || null,
    qualificationState: row?.qualification_state || null,
    opportunityStatus,
    opportunityStatusLabel: outcomeLabel(outcome),
    opportunityStatusHint: outcomeLabel(outcome),
    reviewReasonLabels: outcome ? [outcomeLabel(outcome)] : [],
    matchStatus: verifiedOutcome
      ? "verified"
      : reviewOutcome || potentialMatch
        ? "review"
        : "rejected",
    // Stage Y P0-E: a contradicted identity makes the numeric spread
    // diagnostic-only. Raw prices stay visible; no eligible spread or profit.
    identityRejected:
      String(row?.qualification || "").toUpperCase() === "IDENTITY_REJECTED"
      || String(row?.match_state || "").toLowerCase() === "identity_rejected",
    // Stage Y P0-D: availability is a three-state fact, never a boolean.
    // Unknown must never render as available.
    availabilityState: row?.availability_state || null,
    availabilityStale: Boolean(row?.availability_stale),
    availabilityObservedAt: row?.availability_observed_at || row?.walmart_checked_at || null,
    matchStatusLabel: outcomeLabel(outcome),
    // Analyzer can resolve a genuine UPC/GTIN or Walmart item id.
    canAnalyze: Boolean(row?.upc || row?.gtin || walmartItemId),
    // Stage X: the evidence a qualifying card must show. `feeCovered` is what
    // separates a verified net profit from a fee-excluded estimate, and
    // `verificationBasis` says HOW identity was established (never a bare %).
    feeCovered: row?.fee_complete === true,
    verificationBasis: row?.identifier_type || (row?.gtin || row?.upc ? "gtin" : null),
    condition: row?.walmart_condition || null,
    availability: row?.walmart_availability || null,
    createdAt: row?.observed_at || run?.created_at || null,
    collectedAt: row?.source_price_observed_at || row?.observed_at || null,
    checkedAt: row?.walmart_checked_at || row?.observed_at || null,
    runId: run?.run_id || null,
  });
}

/**
 * Filter buckets:
 * - qualified: financial WINNING opportunities only
 * - estimated: fee/availability-incomplete profitable verified matches
 * - needs_review: NEEDS_REVIEW (not Estimated)
 * - matched: identity-verified matches only (never candidate previews)
 * - rejected: no usable match candidate
 */
export function leadBucket(lead) {
  if (lead.opportunityStatus === "WINNING") return "qualified";
  if (lead.opportunityStatus === "ESTIMATED") return "estimated";
  if (lead.matchLabelCode === "exact" || lead.matchState === "verified_match") {
    return "matched";
  }
  if (
    lead.matchLabelCode === "potential"
    || lead.matchLabelCode === "pack_differs"
    || lead.matchLabelCode === "variant_differs"
    || lead.matchState === "potential_match"
    || lead.potentialMatch
  ) {
    return "potential";
  }
  if (lead.opportunityStatus === "NEEDS_REVIEW") {
    return "needs_review";
  }
  // A review-status comparison that HAS a Walmart listing is a relevant
  // Potential Match — the customer's default tab includes it.
  if (
    REVIEW_MATCH_STATUSES.has(String(lead.matchStatus || "").toLowerCase())
    && (lead.walmartItemId || lead.walmartTitle)
  ) {
    return "potential";
  }
  if (REVIEW_MATCH_STATUSES.has(String(lead.matchStatus || "").toLowerCase())) {
    return "needs_review";
  }
  if (VERIFIED_MATCH_STATUSES.has(String(lead.matchStatus || "").toLowerCase())) {
    return "matched";
  }
  // Never promote an unlabeled Walmart item id to Verified Matches.
  return "rejected";
}

/** Default customer tab: verified + relevant potential Walmart candidates. */
export function isWalmartMatchLead(lead) {
  const b = leadBucket(lead);
  return b === "matched" || b === "potential" || b === "qualified" || b === "estimated";
}


function ResultsTable({ leads }) {
  return (
    <div className="as-results-table-wrap" data-testid="as-results-table">
      <table className="as-results-table">
        <thead>
          <tr>
            <th>Source</th>
            <th>Walmart</th>
            <th>Buy</th>
            <th>Sell</th>
            <th>Profit</th>
            <th>ROI</th>
            <th>Margin</th>
            <th>Match</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {leads.map((lead) => (
            <tr key={lead.id}>
              <td>
                <div className="font-semibold line-clamp-2">{lead.sourceTitle || "—"}</div>
                <div className="as-helper">{lead.sourceMerchant || "—"}</div>
              </td>
              <td className="line-clamp-2">{lead.walmartTitle || lead.walmartItemId || "—"}</td>
              <td className="tabular-nums">{money(lead.sourcePrice, lead.sourceCurrency)}</td>
              <td className="tabular-nums">{money(lead.walmartPrice, lead.walmartCurrency)}</td>
              <td className="tabular-nums">{money(lead.profit, lead.sourceCurrency)}</td>
              <td className="tabular-nums">
                {lead.roi != null && Number.isFinite(Number(lead.roi)) ? `${Number(lead.roi).toFixed(1)}%` : "—"}
              </td>
              <td className="tabular-nums">
                {lead.margin != null && Number.isFinite(Number(lead.margin))
                  ? `${Number(lead.margin).toFixed(1)}%`
                  : "—"}
              </td>
              <td className="tabular-nums">
                {lead.matchConfidence != null ? `${Math.round(lead.matchConfidence)}%` : "—"}
              </td>
              <td>{lead.opportunityStatusLabel || lead.matchStatusLabel || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AutoSourcingResults({
  run,
  sources = [],
  opportunities = [],
  error,
  onRefresh,
  onViewHistory,
  onAnalyze,
  onSave,
  onOpen,
  onResume,
  onPause,
  onStop,
}) {
  const [filter, setFilter] = useState("walmart_matches");  const [retailerFilter, setRetailerFilter] = useState("all");
  const [view, setView] = useState("cards");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("observed_at");
  const [order, setOrder] = useState("desc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [detailIndex, setDetailIndex] = useState(null);
  const [detailHistory, setDetailHistory] = useState([]);
  const [serverPage, setServerPage] = useState({
    items: [],
    total: 0,
    counts: null,
    next_cursor: null,
    loading: false,
    useServer: false,
    criteriaConfigured: null,
  });
  // Offset→cursor map so numbered pages work with keyset pagination.
  const [cursorByPage, setCursorByPage] = useState({ 1: null });
  // ── Live Results (Phase 6) state — declared before the fetch effect ───────
  const [liveTick, setLiveTick] = useState(0);
  const [liveStalled, setLiveStalled] = useState(false);
  const [pendingNew, setPendingNew] = useState(0);
  const seenCollectedRef = useRef(null);

  const runSourceIds = useMemo(
    () => (Array.isArray(run?.source_ids) ? run.source_ids.filter(Boolean) : []),
    [run?.source_ids],
  );
  const multiRetailer = runSourceIds.length > 1;

  const opportunityLeads = useMemo(
    () => (Array.isArray(opportunities) ? opportunities.map(mapDealFlowLead) : []),
    [opportunities],
  );

  useEffect(() => {
    setPage(1);
    setCursorByPage({ 1: null });
  }, [filter, retailerFilter, query, pageSize, sort, order, run?.run_id]);

  // Restore the view the customer left for THIS job. Scoped per run, so job A's
  // filter is never applied to job B, and returning to job A restores exactly
  // what was on screen. Pagination is deliberately reset to page 1: the grid is
  // cursor-paginated, and restoring a page number without its cursors would show
  // the wrong rows rather than simply fewer.
  useEffect(() => {
    const rid = run?.run_id || null;
    const saved = readFilters(rid);
    setFilter(saved.filter || "walmart_matches");
    setRetailerFilter(saved.retailerFilter || "all");
    setView(saved.view || "cards");
    setQuery(saved.query || "");
    setSort(saved.sort || "observed_at");
    setOrder(saved.order || "desc");
    setPageSize(Number(saved.pageSize) || 50);
    setPage(1);
  }, [run?.run_id]);

  // Persist the customer's view as it changes.
  useEffect(() => {
    writeFilters(run?.run_id, { filter, retailerFilter, view, query, sort, order, pageSize });
  }, [run?.run_id, filter, retailerFilter, view, query, sort, order, pageSize]);

  // Stage W: when the customer configured mandatory rules, Results opens on the
  // qualifying grid and STAYS there even when nothing qualifies. Silently
  // switching to "every Walmart match" is the behaviour this prevents.
  const criteriaConfigured = Boolean(serverPage.criteriaConfigured);
  useEffect(() => {
    if (serverPage.criteriaConfigured == null || !criteriaConfigured) return;
    setFilter((current) =>
      current === "walmart_matches" || current === "qualifying" ? "qualifying" : current,
    );
  }, [criteriaConfigured, serverPage.criteriaConfigured]);

  useEffect(() => {
    const runId = run?.run_id;
    if (!runId) {
      setServerPage((s) => ({ ...s, useServer: false, items: [], total: 0, counts: null }));
      return undefined;
    }
    let cancelled = false;
    const cursor = cursorByPage[page] ?? null;
    setServerPage((s) => ({ ...s, loading: true }));
    import("@/lib/autoSourcing/api")
      .then(({ listAutoSourcingRunProducts }) =>
        listAutoSourcingRunProducts(runId, {
          bucket: filter === "all" ? undefined : filter,
          source_id: retailerFilter === "all" ? undefined : retailerFilter,
          q: query.trim() || undefined,
          limit: pageSize,
          cursor: cursor || undefined,
          sort,
          order,
        }),
      )
      .then((data) => {
        if (cancelled) return;
        const items = Array.isArray(data?.items) ? data.items : [];
        const hasTotal = data?.total != null;
        const total = hasTotal ? Number(data.total) || 0 : null;
        // Fall back to embedded diagnostics when durable store is empty (legacy runs).
        // Treat missing total + empty items the same as total=0 (tests / older APIs).
        if (
          items.length === 0
          && (total == null || total === 0)
          && Array.isArray(run?.product_outcomes)
          && run.product_outcomes.length
        ) {
          setServerPage({
            items: [],
            total: 0,
            counts: null,
            next_cursor: null,
            loading: false,
            useServer: false,
          });
          return;
        }
        const next = data?.next_cursor || null;
        if (next) {
          setCursorByPage((prev) =>
            prev[page + 1] === next ? prev : { ...prev, [page + 1]: next },
          );
        }
        // Keyset pages may omit total/counts (Stage E); keep first-page values.
        setServerPage((prev) => ({
          items,
          total: hasTotal ? total : prev.total,
          counts: data?.counts || prev.counts,
          next_cursor: next,
          loading: false,
          useServer: true,
          criteriaConfigured:
            data?.criteria_configured != null
              ? Boolean(data.criteria_configured)
              : prev.criteriaConfigured,
        }));
        setLiveStalled(false);
      })
      .catch(() => {
        if (cancelled) return;
        // A transient read failure must not blank the Results the customer is
        // already reading; flag the live channel and keep the last page.
        setLiveStalled(true);
        setServerPage((prev) =>
          prev.useServer
            ? { ...prev, loading: false }
            : {
                items: [],
                total: 0,
                counts: null,
                next_cursor: null,
                loading: false,
                useServer: false,
              },
        );
      });
    return () => {
      cancelled = true;
    };
    // cursorByPage[page] is read intentionally; full map omitted to avoid fetch loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    run?.run_id,
    run?.updated_at,
    filter,
    retailerFilter,
    query,
    page,
    pageSize,
    sort,
    order,
    cursorByPage[page],
    liveTick,
  ]);

  const outcomeLeads = useMemo(() => {
    if (serverPage.useServer) {
      return serverPage.items.map((row, idx) => mapProductOutcomeToLead(row, run, idx));
    }
    const rows = Array.isArray(run?.product_outcomes) ? run.product_outcomes : [];
    // Prefer opportunity cards when a match was persisted; outcomes fill gaps.
    const oppKeys = new Set(
      opportunityLeads
        .map((l) => String(l.walmartItemId || l.sourceTitle || "").toLowerCase())
        .filter(Boolean),
    );
    return rows
      .map((row, idx) => mapProductOutcomeToLead(row, run, idx))
      .filter((lead) => {
        const key = String(lead.walmartItemId || lead.sourceTitle || "").toLowerCase();
        return !key || !oppKeys.has(key);
      });
  }, [run, opportunityLeads, serverPage]);

  const baseLeads = useMemo(() => {
    if (serverPage.useServer) return outcomeLeads;
    return [...opportunityLeads, ...outcomeLeads];
  }, [opportunityLeads, outcomeLeads, serverPage.useServer]);

  // One authoritative saved state for every card on this page. `saved` is
  // derived from the backend-confirmed store, never from a local toggle.
  const savedStore = useSavedLeads(baseLeads);
  const allLeads = useMemo(
    () =>
      baseLeads.map((lead) => ({
        ...lead,
        saved: savedStore.isSaved(lead),
        saving: savedStore.isPending(lead),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [baseLeads, savedStore.savedKeys, savedStore.pendingKeys],
  );

  // Saving is a real, persisted mutation. A failure surfaces the actual error
  // and the store restores the previous (unsaved) state.
  const handleSave = useCallback(
    async (lead) => {
      if (onSave) return onSave(lead);
      const result = await savedStore.toggle(lead);
      if (!result.ok && result.error) toast.error(result.error);
      else if (result.ok && !result.pending) {
        toast.success(result.saved ? "Saved to Saved Leads." : "Removed from Saved Leads.");
      }
      return result;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onSave],
  );

  const counts = useMemo(() => {
    if (serverPage.useServer && serverPage.counts) {
      const matchedN = Number(serverPage.counts.matched) || 0;
      const potentialN = Number(serverPage.counts.potential) || 0;
      return {
        all: Number(serverPage.counts.all) || 0,
        // Customer default tab: verified + relevant potential Walmart candidates.
        walmart_matches:
          Number(serverPage.counts.walmart_matches) || matchedN + potentialN,
        matched: matchedN,
        potential: potentialN,
        positive_spread: Number(serverPage.counts.positive_spread) || 0,
        qualified: Number(serverPage.counts.qualified) || 0,
        estimated: Number(serverPage.counts.estimated) || 0,
        needs_review: Number(serverPage.counts.needs_review) || 0,
        rejected: Number(serverPage.counts.rejected) || 0,
        // Section 3: the zero-results state must be ACCURATE. These buckets were
        // never mapped, so "N candidates were evaluated…" never rendered even
        // though the server reported the counts.
        disqualified: Number(serverPage.counts.disqualified) || 0,
        insufficient_evidence: Number(serverPage.counts.insufficient_evidence) || 0,
        identity_rejected: Number(serverPage.counts.identity_rejected) || 0,
        no_match: Number(serverPage.counts.no_match) || 0,
      };
    }
    const c = {
      all: allLeads.length,
      walmart_matches: 0,
      matched: 0,
      potential: 0,
      positive_spread: 0,
      qualified: 0,
      estimated: 0,
      needs_review: 0,
      rejected: 0,
    };
    for (const lead of allLeads) {
      const b = leadBucket(lead);
      c[b] = (c[b] || 0) + 1;
      if (isWalmartMatchLead(lead)) c.walmart_matches += 1;
      if (lead.spread != null && Number(lead.spread) > 0) c.positive_spread += 1;
    }
    return c;
  }, [allLeads, serverPage]);

  const filtered = useMemo(() => {
    if (serverPage.useServer) return allLeads;
    const q = query.trim().toLowerCase();
    return allLeads.filter((lead) => {
      if (filter === "walmart_matches" && !isWalmartMatchLead(lead)) return false;
      if (filter === "matched" && leadBucket(lead) !== "matched") return false;
      if (filter === "potential" && leadBucket(lead) !== "potential") return false;
      if (filter === "positive_spread" && !(lead.spread != null && Number(lead.spread) > 0)) {
        return false;
      }
      if (filter === "qualified" && lead.opportunityStatus !== "WINNING") return false;
      if (filter === "estimated" && leadBucket(lead) !== "estimated") return false;
      if (filter === "needs_review" && leadBucket(lead) !== "needs_review") {
        return false;
      }
      // Rejected includes candidate-preview rows that failed identity/qualification.
      if (filter === "rejected" && leadBucket(lead) !== "rejected") return false;
      if (retailerFilter !== "all") {
        const sid = String(lead.sourceId || lead.source_id || "").toLowerCase();
        const merchant = String(lead.sourceMerchant || "").toLowerCase();
        const wanted = sources.find((s) => s.source_id === retailerFilter);
        const name = String(wanted?.name || wanted?.domain || retailerFilter).toLowerCase();
        if (sid !== String(retailerFilter).toLowerCase() && !merchant.includes(name.split(".")[0])) {
          return false;
        }
      }
      if (!q) return true;
      const hay = [
        lead.sourceTitle,
        lead.walmartTitle,
        lead.sourceMerchant,
        lead.upc,
        lead.gtin,
        lead.walmartItemId,
        lead.opportunityStatusLabel,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [allLeads, filter, query, retailerFilter, sources, serverPage.useServer]);

  const filterTotal = serverPage.useServer
    ? Number(serverPage.total) || 0
    : filtered.length;
  const totalPages = Math.max(1, Math.ceil(filterTotal / pageSize) || 1);
  const safePage = Math.min(page, totalPages);
  const paged = useMemo(() => {
    if (serverPage.useServer) return filtered;
    const start = (safePage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, safePage, pageSize, serverPage.useServer]);

  const collected = Number(run?.progress?.products_collected) || filterTotal || allLeads.length;
  const matched = Number(run?.progress?.matched) || counts.matched;
  const potential = Number(run?.progress?.potential) || counts.potential || 0;
  const walmartMatches = matched + potential;

  // ── Live Results (Phase 6) ────────────────────────────────────────────────
  // While the scan is running, poll the durable Results page so new products
  // appear without a manual refresh. The interval stops on terminal states.
  const running = ["running", "collecting", "matching", "verifying", "queued"].includes(
    String(run?.status || "").toLowerCase(),
  );

  // Stage K/L: durable discovery facts for the dashboard activity line.
  const liveProgress = (() => {
    if (!running) return null;
    const parts = [];
    const queued = Number(run?.progress?.pending_match_backlog);
    if (Number.isFinite(queued) && queued > 0) parts.push(`${queued} awaiting match`);
    return parts.length ? parts.join(" · ") : null;
  })();

  useEffect(() => {
    if (!running || !run?.run_id) return undefined;
    const id = setInterval(() => setLiveTick((t) => t + 1), 6000);
    return () => clearInterval(id);
  }, [running, run?.run_id]);

  // Track how many newly discovered products are waiting behind the user's
  // current scroll position so cards never jump while browsing.
  useEffect(() => {
    if (!running) {
      seenCollectedRef.current = null;
      setPendingNew(0);
      return;
    }
    const now = Number(run?.progress?.products_collected) || 0;
    if (seenCollectedRef.current == null) {
      seenCollectedRef.current = now;
      return;
    }
    if (now > seenCollectedRef.current && (page !== 1 || (window.scrollY || 0) > 240)) {
      setPendingNew(now - seenCollectedRef.current);
      seenCollectedRef.current = now;
    } else if (now > seenCollectedRef.current) {
      seenCollectedRef.current = now;
      setPendingNew(0);
    }
  }, [run?.progress?.products_collected, running, page]);

  const applyPendingNew = () => {
    setPendingNew(0);
    setPage(1);
    setLiveTick((t) => t + 1);
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const openDetail = (lead) => {
    const idx = filtered.findIndex((l) => l.id === lead?.id);
    if (idx < 0) return;
    setDetailIndex(idx);
    setDetailHistory([]);
    // Persisted Quick Winner / AS opportunities may have history; outcome-* ids do not.
    if (lead?.id && !String(lead.id).startsWith("outcome-")) {
      import("@/lib/api")
        .then(({ api }) => api.productSourcingHistory(lead.id))
        .then((res) => {
          const data = res?.data ?? res ?? {};
          setDetailHistory(Array.isArray(data.items) ? data.items : []);
        })
        .catch(() => setDetailHistory([]));
    }
  };

  if (!run) {
    return (
      <div className="as-helper" data-testid="auto-sourcing-results-empty">
        Select a run from History, or start a search to see results.
      </div>
    );
  }

  return (
    <div data-testid="auto-sourcing-results-panel">
      <RunSummaryCard
        run={run}
        sources={sources}
        onRefresh={onRefresh}
        onResume={onResume}
        onViewHistory={onViewHistory}
        onPause={onPause}
        onStop={onStop}
        activityExtra={liveProgress}
        durableCounts={serverPage.useServer ? counts : null}
      />

      {liveStalled || pendingNew > 0 ? (
        <div className="as-live-bar" data-testid="as-live-indicator">
          {liveStalled ? (
            <span className="as-live-state is-stalled" data-testid="as-live-stalled">
              Live updates paused — reconnecting…
            </span>
          ) : null}
          {pendingNew > 0 ? (
            <button type="button" className="as-live-new" onClick={applyPendingNew}>
              {pendingNew} new {pendingNew === 1 ? "result" : "results"} — show
            </button>
          ) : null}
        </div>
      ) : null}

      {/* Stage L acceptance: the dashboard above already states status, counts
          and match split — no second full-width status paragraph. */}
      {walmartMatches === 0 && collected > 0 ? (
        <div className="as-results-summary" data-testid="as-results-summary">
          <span>
            {collected} product{collected === 1 ? "" : "s"} discovered
            {counts.all > collected ? ` (${counts.all} total)` : ""} — Walmart
            matching in progress.
          </span>
        </div>
      ) : null}

      {error && (
        <div className="as-inline-error" data-testid="auto-sourcing-results-error">
          <span>{error}</span>
          <button type="button" className="as-retry" onClick={onRefresh}>
            Retry
          </button>
        </div>
      )}

      {multiRetailer ? (
        <div className="as-retailer-result-filters" role="tablist" aria-label="Retailer filters" data-testid="as-retailer-filters">
          <button
            type="button"
            role="tab"
            aria-selected={retailerFilter === "all"}
            className={cn("as-results-filter", retailerFilter === "all" && "is-active")}
            onClick={() => setRetailerFilter("all")}
          >
            All Retailers
          </button>
          {runSourceIds.map((sid) => {
            const src = sources.find((s) => s.source_id === sid);
            return (
              <button
                key={sid}
                type="button"
                role="tab"
                aria-selected={retailerFilter === sid}
                className={cn("as-results-filter", retailerFilter === sid && "is-active")}
                onClick={() => setRetailerFilter(sid)}
                data-testid={`as-retailer-filter-${sid}`}
              >
                {src?.name || src?.domain || sid}
              </button>
            );
          })}
        </div>
      ) : null}

      <div className="as-results-toolbar">
        <div className="as-results-filters" role="tablist" aria-label="Result filters">
          {RESULT_FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              role="tab"
              aria-selected={filter === f.id}
              className={cn("as-results-filter", filter === f.id && "is-active")}
              onClick={() => setFilter(f.id)}
              data-testid={`as-filter-${f.id}`}
            >
              {f.label}
              <span className="as-results-filter-count">{counts[f.id] ?? 0}</span>
            </button>
          ))}
        </div>
        <div className="as-results-toolbar-right">
          <label className="as-results-search">
            <Search className="w-3.5 h-3.5" aria-hidden="true" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Title or identifier"
              aria-label="Filter by title or identifier"
            />
          </label>
          <div className="as-results-sort" data-testid="as-results-sort">
            <label>
              <span className="sr-only">Sort by</span>
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                aria-label="Sort results by"
                data-testid="as-results-sort-field"
              >
                <option value="observed_at">Observed</option>
                <option value="source_price">Buy price</option>
                <option value="walmart_price">Sell price</option>
                <option value="profit">Profit</option>
                <option value="roi">ROI</option>
                <option value="margin">Margin</option>
              </select>
            </label>
            <button
              type="button"
              className="as-results-sort-order"
              aria-label={order === "desc" ? "Sorted descending; activate for ascending" : "Sorted ascending; activate for descending"}
              aria-pressed={order === "desc"}
              title={order === "desc" ? "Descending" : "Ascending"}
              data-testid="as-results-sort-order"
              onClick={() => setOrder((o) => (o === "desc" ? "asc" : "desc"))}
            >
              {order === "desc" ? "↓" : "↑"}
            </button>
          </div>
          <div className="as-results-view-toggle" role="group" aria-label="View mode">
            <button
              type="button"
              className={cn(view === "cards" && "is-active")}
              aria-pressed={view === "cards"}
              onClick={() => setView("cards")}
              title="Cards"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button
              type="button"
              className={cn(view === "table" && "is-active")}
              aria-pressed={view === "table"}
              onClick={() => setView("table")}
              title="Table"
            >
              <Table2 className="w-4 h-4" />
            </button>
          </div>
          <button
            type="button"
            className="as-explore-more is-gated"
            data-testid="as-explore-more"
            disabled={!EXPLORE_MORE_ENABLED}
            title="Explore More Products unlocks after continuation tests pass. It reuses checkpoints without granting extra credits."
            aria-label="Explore More Products (coming soon)"
          >
            {EXPLORE_MORE_ENABLED ? "Explore More" : "Explore More · Soon"}
          </button>
        </div>
      </div>

      {/* Section 3: a non-qualifying tab is a RESEARCH view. Say so up front, so
          a negative-spread comparison is never mistaken for a winner — even when
          the tab happens to be empty. */}
      {filter !== "qualifying" ? (
        <div
          className="as-research-note"
          data-testid="as-research-view-note"
          data-filter={filter}
        >
          Research view — these are not verified profitable opportunities.
          {criteriaConfigured
            ? " Only products meeting your requirements appear in Qualifying."
            : ""}
        </div>
      ) : null}

      {allLeads.length === 0 ? (
        <div data-testid="auto-sourcing-results-none">
          {filter === "qualifying" && Number(counts.all || 0) > 0 ? (
            // Stage W: the qualifying grid is empty because nothing met the
            // customer's rules. Say so — never fall back to every match.
            <div className="as-helper" data-testid="as-qualifying-empty">
              <div className="font-semibold text-ink-primary">
                No products met the required product verification and profitability checks.
              </div>
              <p className="mt-1" data-testid="as-qualifying-empty-next">
                Try adjusting your filters or scanning additional approved categories.
              </p>
              {Number(counts.disqualified || 0) > 0 && (
                <p className="mt-1 text-ink-muted" data-testid="as-qualifying-empty-count">
                  {counts.disqualified} candidate{counts.disqualified === 1 ? "" : "s"} were
                  evaluated and did not meet every requirement.
                </p>
              )}
              {Number(counts.insufficient_evidence || 0) > 0 && (
                <p className="mt-1 text-ink-muted" data-testid="as-qualifying-empty-evidence">
                  {counts.insufficient_evidence} more could not be assessed because product identity,
                  pricing or fee data is missing — they are not counted as unprofitable.
                </p>
              )}
              {Number(counts.identity_rejected || 0) > 0 && (
                <p className="mt-1 text-ink-muted" data-testid="as-qualifying-empty-identity">
                  {counts.identity_rejected} had a product identity, pack, variant or condition that
                  did not match the Walmart listing.
                </p>
              )}
            </div>
          ) : ["running", "collecting", "matching", "queued", "verifying"].includes(
            String(run?.status || "").toLowerCase(),
          ) && collected === 0 ? (
            <>
              <div className="as-helper mb-3">
                <div className="font-semibold text-ink-primary" data-testid="as-empty-title">
                  Collecting products…
                </div>
                <p className="mt-1" data-testid="as-empty-next">
                  Real cards appear as soon as products are persisted. Counts update live.
                </p>
              </div>
              <ResultsSkeletonGrid />
            </>
          ) : collected === 0 ? (
            (() => {
              const copy = emptyDiscoveryCopy(run);
              return (
                <div className="as-helper">
                  <div className="font-semibold text-ink-primary" data-testid="as-empty-title">
                    {copy.title}
                  </div>
                  <p className="mt-1" data-testid="as-empty-next">
                    {copy.next}
                  </p>
                </div>
              );
            })()
          ) : (
            <div className="as-helper">
              Products were collected but no outcome rows are available yet. Refresh to reload.
            </div>
          )}
        </div>
      ) : filtered.length === 0 ? (
        <div className="as-helper" data-testid="as-results-filter-empty">
          {filter === "qualifying" ? (
            // Truthful empty state: nothing met the customer's rules. Never fall
            // back to showing unprofitable matches instead.
            <div data-testid="as-qualifying-empty">
              <div className="font-semibold text-ink-primary">
                No products met the required product verification and profitability checks.
              </div>
              <p className="mt-1" data-testid="as-qualifying-empty-next">
                Try adjusting your filters or scanning additional approved categories.
              </p>
              {counts.disqualified > 0 && (
                <p className="mt-1 text-ink-muted" data-testid="as-qualifying-empty-count">
                  {counts.disqualified} candidate{counts.disqualified === 1 ? "" : "s"} were
                  evaluated and did not meet every requirement.
                </p>
              )}
              {Number(counts.insufficient_evidence || 0) > 0 && (
                <p className="mt-1 text-ink-muted" data-testid="as-qualifying-empty-evidence">
                  {counts.insufficient_evidence} more could not be assessed because product identity,
                  pricing or fee data is missing — they are not counted as unprofitable.
                </p>
              )}
              {Number(counts.identity_rejected || 0) > 0 && (
                <p className="mt-1 text-ink-muted" data-testid="as-qualifying-empty-identity">
                  {counts.identity_rejected} had a product identity, pack, variant or condition that
                  did not match the Walmart listing.
                </p>
              )}
            </div>
          ) : counts.all > 0 ? (
            <span data-testid="as-results-view-all-hint">
              {filter === "walmart_matches" || filter === "matched" || filter === "potential"
                ? `${counts.all} product${counts.all === 1 ? "" : "s"} discovered`
                : "No products match this filter"}
              {" — "}
              <button
                type="button"
                className="as-inline-link"
                onClick={() => setFilter("all")}
                data-testid="as-results-view-all"
              >
                View All Candidates
              </button>
            </span>
          ) : (
            "No products match this filter."
          )}
        </div>
      ) : (
        <>
          {view === "table" ? (
            <ResultsTable leads={paged} />
          ) : (
            <div
              className="df-grid grid gap-2.5 grid-cols-[repeat(auto-fill,minmax(212px,1fr))] pb-4"
              data-testid="as-results-grid"
            >
              {paged.map((lead) => (
                <DealFlowCard
                  key={lead.id}
                  // Any tab other than Qualifying is a RESEARCH view: the card
                  // must state the persisted verdict so a rejected product can
                  // never be mistaken for a winner.
                  lead={{ ...lead, researchView: filter !== "qualifying" }}
                  onSave={handleSave}
                  onAnalyze={onAnalyze || (() => {})}
                  onOpen={openDetail}
                />
              ))}
            </div>
          )}
          <AutoSourcingPagination
            page={safePage}
            pageSize={pageSize}
            total={filterTotal}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </>
      )}

      {detailIndex != null && filtered[detailIndex] ? (
        <OpportunityDetailDialog
          lead={filtered[detailIndex]}
          list={filtered}
          index={detailIndex}
          onIndex={(i) => {
            setDetailIndex(i);
            setDetailHistory([]);
            const next = filtered[i];
            if (next?.id && !String(next.id).startsWith("outcome-")) {
              import("@/lib/api")
                .then(({ api }) => api.productSourcingHistory(next.id))
                .then((res) => {
                  const data = res?.data ?? res ?? {};
                  setDetailHistory(Array.isArray(data.items) ? data.items : []);
                })
                .catch(() => setDetailHistory([]));
            }
          }}
          onClose={() => setDetailIndex(null)}
          onSave={handleSave}
          onRefresh={async () => {
            if (typeof onRefresh === "function") onRefresh();
          }}
          history={detailHistory}
        />
      ) : null}
    </div>
  );
}
